import React, { useMemo, useState } from 'react'
import { resolveSurvey } from '../content/surveyResolver'
import { screenText } from '../content/instructions'
import RichText from '../components/RichText'

// Renders the surveyQuestions_v2 schema. Long lists (≥5 questions) go one question
// at a time with a progress bar + Next button; short lists render on a single page.
export default function SurveyStep ({ step, onAdvance, busy }) {
  const questions = useMemo(() => resolveSurvey(step.survey), [step.survey])
  // Full intro screen, not just its first line. This used to keep only the header on the
  // grounds of dropping a "verbose preamble" — but none of the three intros in the flow
  // has one, and truncating threw away the sentences that do the work:
  //   M1 — "a baseline, not a test you can fail … use 'I'm not sure' freely"
  //   M6 — "your answer does not affect your payment", which is what the honesty item
  //        about external resource use depends on to get a truthful answer.
  const intro = step.intro ? screenText(step.intro) : ''
  const [answers, setAnswers] = useState({})

  const set = (k, v) => setAnswers(a => ({ ...a, [k]: v }))
  const isVisible = (q) => !q.showIf || answers[q.showIf.key] === q.showIf.value
  const answered = (q) => q.optional || (answers[q.key] !== undefined && answers[q.key] !== '')

  const field = (q) => (
    <div className="study-q" key={q.key}>
      <div className="study-q__label">{q.label}</div>
      {q.subtitle && <div className="study-q__sub">{q.subtitle}</div>}

      {q.type === 'scale' && (
        // Buttons grouped, so the two end labels are siblings of one block rather than of
        // seven. Flat, they were wrap candidates like any other flex item — and the max
        // label wrapping put "Very difficult" at the START of the next line, i.e. under
        // "Very easy", inverting the scale it was meant to anchor.
        <div className="study-scale">
          <span className="study-scale__end">{q.minLabel}</span>
          <div className="study-scale__btns">
            {Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i).map(n => (
              <button key={n} className={`study-scale__btn${answers[q.key] === n ? ' is-on' : ''}`} onClick={() => set(q.key, n)}>{n}</button>
            ))}
          </div>
          <span className="study-scale__end study-scale__end--max">{q.maxLabel}</span>
        </div>
      )}

      {(q.type === 'radio' || q.type === 'multiple_choice') && (
        <div className="study-options">
          {q.options.map(opt => (
            <label key={opt} className={`study-opt${answers[q.key] === opt ? ' is-on' : ''}`}>
              <input type="radio" name={q.key} checked={answers[q.key] === opt} onChange={() => set(q.key, opt)} />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}

      {q.type === 'text' && (
        <textarea className="study-text" placeholder={q.placeholder || ''} value={answers[q.key] || ''} onChange={e => set(q.key, e.target.value)} />
      )}
    </div>
  )

  // One scrollable page, always. Long surveys used to go one question at a time with a
  // progress bar — eight clicks to read eight questions, and no way to look back at what
  // you'd already answered. Numbered so the set still reads as a finite list.
  const visible = questions.filter(isVisible)
  const missing = visible.filter(q => !answered(q))
  return (
    <div className="study-card">
      {intro && <div className="study-intro"><RichText text={intro} /></div>}
      <div className="study-survey">
        {visible.map((q, i) => (
          <div className="study-qwrap" key={q.key}>
            <span className="study-qwrap__n">{i + 1}</span>
            <div className="study-qwrap__body">{field(q)}</div>
          </div>
        ))}
      </div>
      <div className="study-actions">
        <button className="study-btn study-btn--primary" disabled={missing.length > 0 || busy} onClick={() => onAdvance(answers)}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
        {missing.length > 0 && <span className="study-hint">{missing.length} question{missing.length > 1 ? 's' : ''} left</span>}
      </div>
    </div>
  )
}
